import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { apiErr, BACKEND_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { PageBanner } from "@/components/ui/PageBanner";
import { InsightsPanel } from "@/components/ui/InsightsPanel";
import { CompClubs } from "@/components/perform/CompClubs";
import { FeedbackInbox } from "@/components/perform/FeedbackVoice";
import { IMG } from "@/lib/assets";
import { Users, Download, Trophy, Activity, Dumbbell, Sparkles, Timer, Clock, Copy, BellRing } from "lucide-react";

const FLAG_META = {
  quiet: { label: "Quiet", color: "#FFB340", bg: "rgba(255,179,64,0.12)" },
  stalled: { label: "Stalled", color: "#FF6B6B", bg: "rgba(255,107,107,0.12)" },
  new: { label: "New", color: "#7C5CFF", bg: "rgba(124,92,255,0.14)" },
  on_track: { label: "On track", color: "#CCFF00", bg: "rgba(204,255,0,0.10)" },
};

function FlagBadge({ flag }) {
  const m = FLAG_META[flag] || FLAG_META.on_track;
  return <span className="mono-label px-2 py-0.5 rounded-full" style={{ color: m.color, background: m.bg }}>{m.label}</span>;
}

export default function CRM() {
  const [selected, setSelected] = useState(null);

  const { data: members = [], isFetching } = useQuery({
    queryKey: ["crm"],
    queryFn: async () => (await api.get("/api/admin/crm")).data,
  });

  const exportCsv = async (clientId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/crm/export${clientId ? `?client_id=${clientId}` : ""}`, { credentials: "include" });
      if (!res.ok) throw new Error("export");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = clientId ? "octon_member_logs.csv" : "octon_all_logs.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV exported");
    } catch (err) { toast.error(apiErr(err, "Export failed")); }
  };

  const active = members.filter((m) => m.total_logs > 0).length;
  const totalLogs = members.reduce((s, m) => s + m.total_logs, 0);

  return (
    <div className="p-4 md:p-8 space-y-6">
      <PageBanner image={IMG.conditioning} height="h-56 md:h-64" overline="admin · analytics" title="Business Insights"
        subtitle="How members use OCTON Gym: sign-ups, revenue, the member journey, popular features and classes, and what to improve next. Plus every member's lifts and PRs."
        actions={
          <Button onClick={() => exportCsv(null)} className="bg-acid text-black hover:bg-[#AACC00] rounded-full shadow-[0_0_30px_rgba(204,255,0,0.35)]" data-testid="crm-export-all-btn">
            <Download className="w-4 h-4" /> Export all (CSV)
          </Button>} />

      <CompClubs />

      <FeedbackInbox />

      <InsightsPanel />

      <div className="pt-4">
        <span className="mono-label text-acid">member by member</span>
        <h2 className="font-heading font-black text-2xl tracking-tight mt-1">Members, lifts and nudges</h2>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Users} label="Members" value={members.length} accent="#3D7BFF" />
        <Stat icon={Activity} label="Logging actively" value={active} accent="#CCFF00" />
        <Stat icon={Dumbbell} label="Total lifts captured" value={totalLogs} accent="#B57BFF" />
        <Stat icon={Trophy} label="Members with PRs" value={members.filter((m) => m.recent_prs.length > 0).length} accent="#FFB340" />
      </div>

      <NudgePanel />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Member list */}
        <div className="lg:col-span-5 space-y-2">
          {isFetching && members.length === 0 && <p className="mono-label animate-pulse">Loading roster…</p>}
          {members.map((m, i) => (
            <motion.button key={m.user_id} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.03 }}
              onClick={() => setSelected(m)} data-testid={`crm-member-${i}`}
              className={`w-full text-left surface rounded-lg p-4 flex items-center gap-3 transition-colors border ${
                selected?.user_id === m.user_id ? "border-acid" : "border-transparent hover:border-[#343A4A]"}`}>
              <div className="w-10 h-10 rounded-full bg-neural/20 border border-neural/40 flex items-center justify-center font-bold shrink-0">
                {m.name?.[0]?.toUpperCase() || "?"}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate flex items-center gap-2">{m.name}<FlagBadge flag={m.flag} /></p>
                <p className="text-xs text-[#8A91A6] truncate">
                  {m.total_logs} lifts · {m.sessions_30d} sessions / 30d
                  {m.last_movement && <span> · last: {m.last_movement}</span>}
                </p>
              </div>
              <div className="text-right shrink-0">
                {m.recent_prs.length > 0 && <span className="mono-label inline-flex items-center gap-1 text-acid"><Trophy className="w-3 h-3" />{m.recent_prs.length}</span>}
                <p className="mono-label mt-0.5">{m.last_activity ? m.last_activity.slice(0, 10) : "—"}</p>
              </div>
            </motion.button>
          ))}
          {!isFetching && members.length === 0 && <p className="text-[#565C6F] text-sm">No members yet.</p>}
        </div>

        {/* Detail */}
        <div className="lg:col-span-7">
          {!selected ? (
            <div className="relative overflow-hidden rounded-xl border border-[#1E222A] p-12 h-full min-h-[18rem] flex flex-col items-center justify-center text-center">
              <img src={IMG.progress} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
              <div className="absolute inset-0" style={{ background: "radial-gradient(circle at center,rgba(5,5,5,0.4),#050505 75%)" }} />
              <Users className="relative w-9 h-9 text-acid mb-3" style={{ filter: "drop-shadow(0 0 10px rgba(204,255,0,0.6))" }} />
              <p className="relative text-[#B7BECB]">Select a member to see their lifts, PRs and OCTON insights.</p>
            </div>
          ) : (
            <MemberDetail member={selected} onExport={() => exportCsv(selected.user_id)} />
          )}
        </div>
      </div>
    </div>
  );
}

function MemberDetail({ member, onExport }) {
  const params = { client_id: member.user_id };
  const { data: prs = [] } = useQuery({ queryKey: ["crm-prs", member.user_id], queryFn: async () => (await api.get("/api/logs/prs", { params })).data });
  const { data: logs = [] } = useQuery({ queryKey: ["crm-logs", member.user_id], queryFn: async () => (await api.get("/api/logs", { params })).data });
  const { data: insights } = useQuery({ queryKey: ["crm-insights", member.user_id], queryFn: async () => (await api.get("/api/logs/insights", { params })).data });
  const areas = insights?.areas || [];

  return (
    <div className="space-y-4" data-testid="crm-detail">
      <div className="surface rounded-lg p-5 flex flex-wrap items-center gap-3 justify-between">
        <div>
          <h2 className="font-heading font-bold text-xl tracking-tight">{member.name}</h2>
          <p className="mono-label mt-1">{member.email} · {member.membership_status}{member.membership_plan ? ` · ${member.membership_plan}` : ""}</p>
        </div>
        <Button onClick={onExport} variant="outline" className="border-[#1E222A] rounded-full h-9" data-testid="crm-export-member-btn">
          <Download className="w-4 h-4" /> Export member
        </Button>
      </div>

      <div className="surface rounded-lg p-5">
        <div className="flex items-center gap-2 mb-3"><Trophy className="w-4 h-4 text-acid" /><span className="font-heading font-bold tracking-tight">Personal records</span></div>
        {prs.length === 0 ? <p className="text-[#565C6F] text-sm">No lifts logged yet.</p> : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {prs.slice(0, 9).map((p) => (
              <div key={p.movement} className="rounded-md border border-[#1E222A] surface-elev p-3">
                <p className="text-sm font-medium truncate">{p.movement}</p>
                <p className="font-heading font-black text-xl text-acid mt-1">
                  {p.best_weight_kg != null ? `${p.best_weight_kg}kg` : (p.best_score || "—")}
                </p>
                {p.best_est_1rm != null && <p className="mono-label mt-0.5">est 1RM {p.best_est_1rm}kg</p>}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="surface rounded-lg p-5">
        <div className="flex items-center gap-2 mb-3"><Sparkles className="w-4 h-4 text-neural" /><span className="font-heading font-bold tracking-tight">OCTON coaching insights</span></div>
        {areas.length === 0 ? <p className="text-[#565C6F] text-sm">Not enough data yet.</p> : (
          <div className="space-y-2">
            {areas.map((a) => (
              <div key={a.title} className="rounded-md border border-[#1E222A] surface-elev p-3">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-medium text-white/90">{a.title}</p>
                  <span className="mono-label shrink-0" style={{ color: (a.confidence ?? 0) > 0.7 ? "#FF3B30" : "#CCFF00" }}>{Math.round((a.confidence ?? 0) * 100)}%</span>
                </div>
                <p className="text-xs text-[#8A91A6] mt-1">{a.insight}</p>
                {a.recommendation && <p className="text-xs text-acid mt-1 flex items-start gap-1"><Timer className="w-3 h-3 mt-0.5 shrink-0" />{a.recommendation}</p>}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="surface rounded-lg p-5">
        <div className="flex items-center gap-2 mb-3"><Clock className="w-4 h-4 text-[#565C6F]" /><span className="font-heading font-bold tracking-tight">Activity log</span></div>
        <div className="space-y-2 max-h-72 overflow-y-auto">
          {logs.slice(0, 40).map((l) => {
            const detail = [];
            if (l.weight_kg != null) detail.push(`${l.weight_kg}kg`);
            if (l.reps != null) detail.push(`${l.sets ? l.sets + "x" : ""}${l.reps}`);
            if (l.score) detail.push(l.score);
            if (l.distance_m != null) detail.push(`${l.distance_m}m`);
            return (
              <div key={l.id} className="flex items-center justify-between border-b border-[#1E222A] py-2 text-sm">
                <div className="min-w-0">
                  <span className="text-white/90">{l.movement}</span>
                  {l.is_pr && <span className="ml-2 text-[10px] font-bold text-black bg-acid rounded px-1.5 py-0.5">PR</span>}
                  <span className="text-[#8A91A6] ml-2 font-mono2 text-xs">{detail.join(" · ")}</span>
                </div>
                <span className="mono-label whitespace-nowrap">{l.date?.slice(0, 10)}</span>
              </div>
            );
          })}
          {logs.length === 0 && <p className="text-[#565C6F] text-sm">No activity yet.</p>}
        </div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent = "#CCFF00" }) {
  return (
    <motion.div whileHover={{ y: -4 }} className="relative overflow-hidden rounded-xl p-5 border border-[#1E222A] bg-[#0F1115] group">
      <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full blur-2xl opacity-25 group-hover:opacity-50 transition-opacity" style={{ background: accent }} />
      <span className="relative w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{ background: `${accent}1F`, border: `1px solid ${accent}55` }}>
        <Icon className="w-5 h-5" style={{ color: accent }} />
      </span>
      <p className="relative font-heading font-black text-3xl" style={{ color: accent }}>{value}</p>
      <p className="relative mono-label mt-1">{label}</p>
    </motion.div>
  );
}

function NudgePanel() {
  const { data, isFetching } = useQuery({
    queryKey: ["crm-nudges"],
    queryFn: async () => (await api.get("/api/admin/crm/nudges")).data,
  });
  const nudges = data?.nudges || [];
  const copy = async (n) => {
    try { await navigator.clipboard.writeText(n.message); toast.success(`Nudge for ${n.name} copied`); }
    catch { toast.error("Copy failed"); }
  };
  if (isFetching && nudges.length === 0) return null;
  if (nudges.length === 0)
    return (
      <div className="surface rounded-lg p-5 flex items-center gap-3" data-testid="nudge-panel">
        <BellRing className="w-5 h-5 text-acid" />
        <p className="text-sm text-[#8A91A6]">Everyone's on track — no members need a nudge right now.</p>
      </div>
    );
  return (
    <div className="surface rounded-lg p-5" data-testid="nudge-panel">
      <div className="flex items-center gap-2 mb-4">
        <BellRing className="w-4 h-4 text-acid" />
        <span className="font-heading font-bold tracking-tight">Needs attention</span>
        <span className="mono-label ml-auto">OCTON flagged {nudges.length} · ready-to-send</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {nudges.map((n) => (
          <div key={n.user_id} className="rounded-md border border-[#1E222A] surface-elev p-3" data-testid="nudge-card">
            <div className="flex items-center justify-between gap-2 mb-2">
              <span className="text-sm font-medium truncate">{n.name}</span>
              <FlagBadge flag={n.flag} />
            </div>
            <p className="text-xs text-[#8A91A6] italic">"{n.message}"</p>
            <div className="flex items-center justify-between mt-2">
              <span className="mono-label">{n.last_movement ? `last: ${n.last_movement}` : "no logs yet"}</span>
              <button onClick={() => copy(n)} className="mono-label inline-flex items-center gap-1 text-acid hover:text-white transition-colors" data-testid="nudge-copy-btn">
                <Copy className="w-3 h-3" /> Copy
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}